import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import PopUpPagamento, { type PopupData } from "./PopUpPagamento";
import { PopupAccordionProvider, usePopupAccordion } from "./PopupAccordionContext";
import { twMerge } from "tailwind-merge";

import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from "../../context/ChatContext";
import { DEV } from "../../lib/dev";
import eventos from "./eventos.json";
const SWIPE_DISTANCIA = 60;
const SWIPE_VELOCIDADE = 400;

const TOLERANCIA_CLIQUE = 5;

const RIGHT_PADRAO = 16;
const RIGHT_COM_CHAT = RIGHT_PADRAO + 364;

const StackingInner = ({
    popups,
    onHidePopup,
}: {
    popups: PopupData[];
    onHidePopup: (id: number) => void;
}) => {
    const ids = popups.map((popup) => popup.id);
    const [hover, setHover] = useState(false);
    const [autoHover, setAutoHover] = useState(false);
    const { openId, toggle } = usePopupAccordion();
    const { isChatOpen } = useChat();

    const height = 50;
    const gap = 4;
    const total = popups.length;

    const isHovered = hover || autoHover;

    const inicioDoGesto = useRef({ x: 0, y: 0 });

    const engolirCliqueDeArrasto = (e: MouseEvent) => {
        const arrastou =
            Math.abs(e.clientX - inicioDoGesto.current.x) > TOLERANCIA_CLIQUE ||
            Math.abs(e.clientY - inicioDoGesto.current.y) > TOLERANCIA_CLIQUE;

        if (!arrastou) return;

        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        if (openId !== null && !ids.includes(openId)) {
            toggle(0);
        }
    }, [openId, ids, toggle]);

    const prevOpenIdTotal = useMemo(() => ({ openId, total }), [openId, total]);
    useEffect(() => {
    }, [prevOpenIdTotal]);

    useEffect(() => {
        if (total <= 1) return;

        const interval = setInterval(() => {
            setAutoHover(true);

            const timeout = setTimeout(() => {
                setAutoHover(false);
            }, 2500);

            return () => clearTimeout(timeout);
        }, 15000);

        return () => clearInterval(interval);
    }, [total]);

    const stackOrder = useMemo(() => {
        if (openId === null || !ids.includes(openId)) return ids;
        return [openId, ...ids.filter((x) => x !== openId)];
    }, [openId, ids]);

    const expandedCount = openId === null ? total : Math.max(total - 1, 0);
    const expandedHeight =
        expandedCount * height + Math.max(0, expandedCount - 1) * gap;

    const handleEnterItem = (id: number) => {
        if (openId === null) {
            setHover(true);
            return;
        }

        setHover(id !== openId);
    };

    return (
        <motion.div
            className={twMerge(
                `fixed right-4`,
                `${popups.length <= 1 ? 'bottom-2' : 'bottom-5'}`
            )}
            initial={{ right: isChatOpen ? RIGHT_COM_CHAT : RIGHT_PADRAO }}
            animate={{ right: isChatOpen ? RIGHT_COM_CHAT : RIGHT_PADRAO }}
        >
            <div
                className="relative w-[380px]"
                style={{ height: isHovered ? expandedHeight : 0 }}
                onMouseLeave={() => setHover(false)}
            >
                <AnimatePresence mode="popLayout">
                    {popups.map((popup) => {
                        const id = popup.id;
                        const stackPos = stackOrder.indexOf(id);

                        const maxOffset = 20;
                        const minOffset = 0;
                        const offsetStep = Math.max(minOffset, maxOffset - total * 3.7);

                        const stackedOffset = -stackPos * offsetStep;

                        let offset = 0;

                        if (!isHovered) {
                            offset = stackedOffset;
                        } else {
                            const baseBottom = -(total - 1) * offsetStep;
                            offset =
                                baseBottom + (total - 1 - stackPos) * (height + gap);
                        }

                        const scale = isHovered ? 1 : 1 - stackPos * 0.05;
                        const opacity = isHovered ? 1 : 1 - stackPos * 0.05;
                        const zIndex = 1000 - stackPos;

                        return (
                            <motion.div
                                key={id}
                                layout
                                initial={{ opacity: 0, x: 40 }}
                                animate={{
                                    bottom: offset,
                                    scale,
                                    opacity,
                                    x: 0,
                                }}
                                exit={{ opacity: 0, x: 400 }}
                                transition={{ duration: 0.2 }}
                                className="absolute right-0 cursor-grab active:cursor-grabbing"
                                onMouseEnter={() => handleEnterItem(id)}
                                onPointerDownCapture={(e) => {
                                    inicioDoGesto.current = { x: e.clientX, y: e.clientY };
                                }}
                                onClickCapture={engolirCliqueDeArrasto}
                                style={{ zIndex }}
                                drag="x"
                                dragDirectionLock
                                dragConstraints={{ left: 0, right: 0 }}
                                // Puxar para a direita (o lado em que o stack fica) é o gesto que
                                // descarta; para a esquerda o card resiste e volta.
                                dragElastic={{ left: 0.1, right: 0.9, top: 0, bottom: 0 }}
                                onDragEnd={(_, info) => {
                                    if (info.offset.x > SWIPE_DISTANCIA || info.velocity.x > SWIPE_VELOCIDADE) {
                                        onHidePopup(id);
                                    }
                                }}
                            >
                                <PopUpPagamento
                                    id={id}
                                    data={popup}
                                    onHidePopup={onHidePopup}
                                />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

const Stacking = () => {
    const [popupsData, setPopupsData] = useState<{ popups: PopupData[] }>({ popups: [] });
    // Remove hiddenPopupIds state, derive it from popupsData

    useEffect(() => {
        const semRemoto = (): PopupData[] => (DEV ? (eventos.popups as PopupData[]) : []);

        const loadPopups = async () => {
            try {
                const res = await fetch("https://jsonhost.com/json/e61e17c1843882b1c69c2cf36d787858");

                if (!res.ok) {
                    setPopupsData({ popups: semRemoto() });
                    return;
                }

                const data = await res.json();
                const remotos: PopupData[] = Array.isArray(data?.popups) ? data.popups : [];

                setPopupsData({ popups: remotos.length ? remotos : semRemoto() });
            } catch (err) {
                console.error("Erro ao carregar popups:", err);
                setPopupsData({ popups: semRemoto() });
            }
        };

        loadPopups();
    }, []);

    const [hiddenPopupIds, setHiddenPopupIds] = useState<number[]>([]);

    const foiFechado = (id: number) =>
        hiddenPopupIds.includes(id) || (!DEV && !!localStorage.getItem(`notshow_notification_${id}`));

    const handleHidePopup = (id: number) => {
        localStorage.setItem(`notshow_notification_${id}`, "true");
        setHiddenPopupIds((prev) => [...prev, id]);
    };

    const popups = useMemo<PopupData[]>(() => {
        const basePopups: PopupData[] = Array.isArray(popupsData?.popups)
            ? popupsData.popups.map((popup) => ({
                ...popup,
                isEvent: true,
            }))
            : [];

        return basePopups.filter((popup) => !foiFechado(popup.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popupsData, hiddenPopupIds]);

    const defaultOpenId = useMemo(() => {
        const defaultPopup = popups.find((popup) => (popup as { open?: string }).open === "true");
        return defaultPopup?.id ?? null;
    }, [popups]);

    return (
        <PopupAccordionProvider defaultOpenId={defaultOpenId}>
            <StackingInner popups={popups} onHidePopup={handleHidePopup} />
        </PopupAccordionProvider>
    );
};

export default Stacking;